<template>
  <!-- This form may be displayed before editedConfiguration is ready -->
  <div
    class="loader is-size-2 mx-auto"
    v-if="
      configurationStore.targetVersionId !== workerVersion.id ||
      !Object.keys(configurationStore.editedConfiguration).length
    "
  ></div>
  <div
    class="notification is-warning"
    v-else-if="fields.length <= 0"
  >
    There are no configuration fields on this worker version.
  </div>
  <template
    v-else
    v-for="fieldDefinition in fields"
  >
    <!--
      The keys on fields and groups include the original configuration ID so that switching configurations
      causes a full reload of the fields, triggering their validation again entirely and avoiding bugs
    -->
    <FieldGroup
      v-if="fieldDefinition.type === 'group'"
      :key="`${configurationStore.originalConfigurationId}-group-${fieldDefinition.key}`"
      :field-key="fieldDefinition.key"
      :field-definition="fieldDefinition"
      :worker-id="workerVersion.worker.id"
      :corpus-id="corpusId"
      :readonly="readonly"
      v-model="configurationStore.editedConfiguration[fieldDefinition.key]"
    />
    <div
      class="field"
      v-else
      :key="`${configurationStore.originalConfigurationId}-${fieldDefinition.key}`"
    >
      <label class="label">{{ fieldDefinition.display_name }}</label>
      <p v-if="fieldDefinition.help_text">
        {{ fieldDefinition.help_text }}
      </p>
      <div class="control">
        <component
          :is="fieldDefinition.many ? ListField : FIELD_COMPONENTS[fieldDefinition.type]"
          :field-key="fieldDefinition.key"
          :field-definition="fieldDefinition"
          :worker-id="workerVersion.worker.id"
          :corpus-id="corpusId"
          v-model="configurationStore.editedConfiguration[fieldDefinition.key]"
        />
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useWorkerConfigurationStore } from '@/stores'
import type { GroupedWorkerConfigurationField } from '@/stores/workerConfiguration'
import type { UUID } from '@/types'
import type { WorkerVersion } from '@/types/worker'

import { FIELD_COMPONENTS } from './Fields'
import FieldGroup from './Fields/FieldGroup.vue'
import ListField from './Fields/ListField.vue'

const {
  workerVersion,
  corpusId,
  readonly = false,
} = defineProps<{ workerVersion: WorkerVersion; corpusId: UUID | null; readonly?: boolean }>()

const configurationStore = useWorkerConfigurationStore()

const fields = computed<GroupedWorkerConfigurationField[]>(() => {
  if (!configurationStore.targetVersionId) return []
  const groupedFields = configurationStore.groupedFields(configurationStore.targetVersionId)
  if (groupedFields === null) return []
  if (readonly) {
    return groupedFields.map(
      (fieldDefinition) =>
        ({
          ...fieldDefinition,
          editable: false,
          default:
            fieldDefinition.default ??
            configurationStore.editedConfiguration[fieldDefinition.key].value,
        }) as GroupedWorkerConfigurationField,
    )
  } else {
    // When editing, only include editable fields
    return groupedFields.filter((fieldDefinition) => fieldDefinition.editable)
  }
})
</script>
